/**
 * Guards of edits after ready (edits.html «Общие правила»):
 *  - lock: no edit while any layer of the story or its translation generates;
 *  - print: no edit of an illustration while its print order is open
 *    (paid, PDF being made — until it is sent or cancelled);
 *  - exports: PDF, print, video cannot be built while their pages or audio
 *    are stale («сначала обновите»);
 *  - re-translate never overwrites blocks the parent edited in the
 *    translation without a diff confirmation.
 *
 * Pure checks + EditGuardService over two ports (EditGuardRepository is the
 * Drizzle side).
 */
import type { LayerKind, LayerState } from '../contracts';
import type { OrderStatus } from '../order/types';
import type { EditExport, StoryEditState, StoryLocks } from './types';

export interface ActiveLayer {
  storyId: string;
  kind: LayerKind;
  state: LayerState;
  orderStatus: OrderStatus;
}

/**
 * A layer is generating when it runs, or waits inside an order that is still
 * running (it starts by itself once its inputs are ready). A button waiting
 * for the parent (awaiting_user) and final states do not lock.
 */
export function isGenerating(l: ActiveLayer): boolean {
  if (l.orderStatus !== 'running') return l.state === 'running';
  return l.state === 'running' || l.state === 'waiting';
}

export function generationLock(layers: ActiveLayer[]): LayerKind[] {
  return [...new Set(layers.filter(isGenerating).map((l) => l.kind))];
}

/**
 * Print orders that freeze the book: paid and not yet sent. A draft is not
 * open — its PDF is made from the book as it is at payment.
 */
export const OPEN_PRINT_STATUSES = ['paid', 'generating_pdf'] as const;

export function openPrintIllustrations(orders: Array<{ bookId: string; status: string }>): string[] {
  return [...new Set(orders.filter((o) => (OPEN_PRINT_STATUSES as readonly string[]).includes(o.status)).map((o) => o.bookId))];
}

/** Keys of the artifacts an export is built from. */
export function exportInputKeys(x: EditExport, state: StoryEditState): string[] {
  const ills = x.illustrationId ? state.illustrations.filter((i) => i.illustrationId === x.illustrationId) : state.illustrations;
  const narrs = x.narrationId ? state.narrations.filter((n) => n.narrationId === x.narrationId) : state.narrations;
  const keys: string[] = [];
  const withPages = x.kind === 'pdf' || x.kind === 'print' || x.kind === 'video' || x.kind === 'pub';
  const withAudio = x.kind === 'video' || x.kind === 'pub' || x.kind === 'yt';
  if (withPages) {
    for (const i of ills) for (const p of i.pages) keys.push(p.kind === 'cover' ? `cover:${i.illustrationId}` : `page:${i.illustrationId}:${p.pageId}`);
  }
  if (withAudio) {
    for (const n of narrs) {
      keys.push(`afile:${n.narrationId}`);
      for (const b of state.blocks) keys.push(`audio:${n.narrationId}:${b.blockId}`);
    }
  }
  return keys;
}

/** An export with stale inputs cannot be built: the stale keys it waits for. */
export function exportBlockedByStale(x: EditExport, state: StoryEditState, staleKeys: Iterable<string>): { blocked: boolean; keys: string[] } {
  const stale = new Set(staleKeys);
  const keys = exportInputKeys(x, state).filter((k) => stale.has(k));
  return { blocked: keys.length > 0, keys };
}

/**
 * «Перевести заново»: blocks the parent edited in the translation that the
 * new translation would overwrite → shown as a diff, need a confirmation.
 */
export function retranslateConflicts(
  translation: Array<{ blockId: string; manuallyEdited: boolean; text: string }>,
  proposed: Array<{ blockId: string; text: string }>,
): Array<{ blockId: string; current: string; proposed: string }> {
  const byId = new Map(proposed.map((p) => [p.blockId, p.text]));
  return translation
    .filter((b) => b.manuallyEdited && byId.has(b.blockId) && byId.get(b.blockId) !== b.text)
    .map((b) => ({ blockId: b.blockId, current: b.text, proposed: byId.get(b.blockId)! }));
}

// ── service over ports ─────────────────────────────────────────────────────

export interface EditGuardPorts {
  /** the story's translations (stories.translated_from_story_id = storyId) */
  translationsOf(storyId: string): Promise<string[]>;
  /** layers of every v2 order of these stories */
  layersOf(storyIds: string[]): Promise<ActiveLayer[]>;
  /** print orders of these illustrations (print_orders.book_id) */
  printOrdersOf(illustrationIds: string[]): Promise<Array<{ bookId: string; status: string }>>;
}

export class EditGuardService {
  constructor(private readonly ports: EditGuardPorts) {}

  async locks(storyId: string, illustrationIds: string[]): Promise<StoryLocks> {
    const translations = await this.ports.translationsOf(storyId);
    const [layers, prints] = await Promise.all([
      this.ports.layersOf([storyId, ...translations]),
      illustrationIds.length ? this.ports.printOrdersOf(illustrationIds) : Promise.resolve([]),
    ]);
    return { generating: generationLock(layers), printOpenFor: openPrintIllustrations(prints) };
  }
}
